import React, { useEffect, useState } from "react";
import {
  Button,
  Modal,
  Space,
  Table,
  Tag,
  Pagination,
  message,
  Drawer,
  Input,
  Form,
  Select,
  DatePicker,
} from "antd";
import { get, post } from "../../apiService";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import "./UserList.css";
import TitleComp from "./TitleComp";
import { getProfile } from "../../services/localStorageServices";
import TextArea from "antd/es/input/TextArea";
import moment from "moment/moment";
import Excel from "../Export/Excel";
import ImportUser from "./Model/ImportUser";

const UserList = () => {
  const [userData, setUserData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [modal, contextHolder] = Modal.useModal();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const { profile } = getProfile();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const [updateDetail, setUpdateDetail] = useState(null);
  const [searchInput, setSearchInput] = useState("");

  const showDrawer = (data) => {
    setOpen(true);
    setUpdateDetail(data);
  };
  const onClose = () => {
    setOpen(false);
  };

  const handleVerify = async (row) => {
    setVerifyLoading(true);
    const verify = row.verify === "1" ? "0" : "1";
    const formData = new FormData();
    formData.append("action", "update_profile");
    formData.append("verify", verify);
    formData.append("user_id", row.user_id);

    try {
      const response = await post("/user.php", formData);
      if (response) {
        message.success("Updated successfully");
        // Re-fetch users to reflect changes
        fetchUsers(pagination.current, pagination.pageSize);
      }
    } catch (error) {
      message.error("Error updating verification. Please try again");
    } finally {
      setVerifyLoading(false);
    }
  };

  const confirm = (value) => {
    modal.confirm({
      title: "Confirm",
      icon: <ExclamationCircleOutlined />,
      content: `${
        value.verify == "1"
          ? "Are you sure you want to unverify?"
          : "Are you sure you want to verify?"
      }`,
      okText: "OK",
      cancelText: "Cancel",
      confirmLoading: verifyLoading,
      onOk: () =>
        new Promise((resolve, reject) => {
          handleVerify(value)
            .then(() => {
              resolve();
              // Optionally, modal.destroy() can be used if you want to manually close it
            })
            .catch(() => {
              reject();
              // Optionally handle errors
            });
        }),
    });
  };

  useEffect(() => {
    const dbounce = setTimeout(() => {
      fetchUsers(pagination.current, pagination.pageSize, searchInput);
    }, 300);
    return () => clearTimeout(dbounce);
  }, [pagination.current, pagination.pageSize, searchInput]);

  const fetchUsers = async (current, pageSize, search) => {
    setLoading(true);
    try {
      const action = "fetch";
      const response = await get(
        `/user.php?action=${action}&limit=${pageSize}&page=${current}&type=user${
          profile.type === "staff" ? "&refer_by=" + profile.user_id : ""
        }${search ? "&search=" + search + "" : ""}`
      );
      if (response) {
        setUserData(response.list);
        setPagination((prevPagination) => ({
          ...prevPagination,
          current,
          pageSize,
          total: response.total,
        }));
        setTotal(response.total);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination) => {
    const { current, pageSize } = newPagination;
    setPagination({
      ...pagination,
      current,
      pageSize,
    });
  };

  const columns = [
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "User Name", dataIndex: "user_name", key: "user_name" },
    { title: "Staff", dataIndex: "staff_name", key: "staff_name" },
    { title: "User Email", dataIndex: "user_email", key: "user_email" },
    { title: "Number", dataIndex: "number", key: "number" },
    {
      title: "Verify Status",
      dataIndex: "verify",
      key: "verify",
      sorter: (a, b) => a.verify - b.verify,
      render: (_, { verify }) => {
        const color = verify == "1" ? "#52c41a" : "#ff4d4f";
        return (
          <Tag color={color}>{verify == "1" ? "Verified" : "Pending"}</Tag>
        );
      },
    },
    {
      title: "Verification Action",
      dataIndex: "verify",
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          <Button
            className="userList-verify-btn bg-dark text-white"
            onClick={() => confirm(record)}
          >
            Action
          </Button>
          <Button
            onClick={() => showDrawer(record)}
            className="userList-verify-btn bg-warning text-white"
          >
            Update
          </Button>
        </Space>
      ),
    },
  ];

  const exportColumn = [
    { title: "Full Name", dataIndex: "name", key: "name" },
    { title: "Phone", dataIndex: "number", key: "number" },
    { title: "Email", dataIndex: "user_email", key: "user_email" },
    { title: "Username", dataIndex: "user_name", key: "user_name" },
    { title: "Gender", dataIndex: "gender", key: "gender" },
    { title: "Date Of Birth", dataIndex: "dob", key: "dob" },
    { title: "Staff ID", dataIndex: "refer_by", key: "refer_by" },
    { title: "Address One", dataIndex: "address_one", key: "address_one" },
    { title: "Address Two", dataIndex: "address_two", key: "address_two" },
    { title: "City", dataIndex: "city", key: "city" },
    { title: "State", dataIndex: "state", key: "state" },
    { title: "Country", dataIndex: "country", key: "country" },
    { title: "Zip Code", dataIndex: "zip_code", key: "zip_code" },
    {
      title: "Business Type",
      dataIndex: "business_activity",
      key: "business_activity",
    },
    { title: "Company Name", dataIndex: "compney_name", key: "compney_name" },
    {
      title: "Business Relationship Period",
      dataIndex: "business_relation_ship_period",
      key: "business_relation_ship_period",
    },
    {
      title: "Promoter Name",
      dataIndex: "promoter_name",
      key: "promoter_name",
    },
    {
      title: "Business Registrarion No",
      dataIndex: "business_registrarion_no",
      key: "business_registrarion_no",
    },
    { title: "Website", dataIndex: "website", key: "website" },
  ];

  useEffect(() => {
    if (updateDetail) {
      form.setFieldsValue({
        ...updateDetail,
        dob:
          updateDetail.dob === "0000-00-00" ? null : moment(updateDetail.dob),
        company_name: updateDetail.company_name?.isValid
          ? updateDetail.company_name
          : null, // Ensure dob is set properly
        website: updateDetail.website?.isValid ? updateDetail.website : null, // Ensure dob is set properly
        business_relationship_period: updateDetail.business_relationship_period
          ?.isValid
          ? updateDetail.business_relationship_period
          : null, // Ensure dob is set properly
        promoter_name: updateDetail.promoter_name?.isValid
          ? updateDetail.promoter_name
          : null, // Ensure dob is set properly
        business_registrarion_no: updateDetail.business_registrarion_no?.isValid
          ? updateDetail.business_registrarion_no
          : null,
      });
    }
  }, [updateDetail]);

  const onFinish = async (value) => {
    try {
      const formData = new FormData();
      Object.entries(value).forEach(([key, val]) => {
        formData.append(
          key,
          key === "dob" ? moment(new Date(val)).format("YYYY-MM-DD") : val
        );
      });
      formData.append("action", "update_profile");
      const resp = await post("/user.php", formData);

      if (resp) {
        const index = userData.findIndex(
          (val) => val.user_id === value.user_id
        );
        userData[index] = value;
        message.success("User Detail Update");
        setOpen(false);
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="p-3">
      <TitleComp
        name="User List"
        type="User"
        totalData={total}
        setSearchInput={setSearchInput}
      />
      <div className="export_import d-flex align-items-center justify-content-between mb-4">
        <ImportUser
          validData={exportColumn.map((val) => val.title)}
          column={exportColumn}
        />
        <Excel
          column={exportColumn}
          data={userData}
          fileName="sejal_users_list"
        />
      </div>
      <div className="d-lg-block d-none">
        <Table
          columns={columns}
          dataSource={userData}
          pagination={false}
          loading={loading}
          onChange={handleTableChange}
        />
      </div>
      <div className="d-lg-none d-block">
        {userData?.map((data) => (
          <div className="table_card">
            {columns?.map((value) => (
              <div
                className="row"
                key={value.key}
                style={{ marginBottom: "10px" }}
              >
                <div className="col-6">{value.title}</div>
                <div className="col-2">:</div>
                <div className="col-4 d-flex justify-content-end">
                  {value.key !== "verify" ? (
                    data[value.key]
                  ) : (
                    <Tag color={data.verify == "1" ? "#52c41a" : "#ff4d4f"}>
                      {data.verify == "1" ? "Verified" : "Pending"}
                    </Tag>
                  )}

                  {value.key === "action" ? (
                    <>
                      <Button
                        className="userList-verify-btn bg-dark text-white mx-2"
                        onClick={() => confirm(data)}
                      >
                        Action
                      </Button>
                      <Button
                        onClick={() => showDrawer(data)}
                        className="userList-verify-btn bg-warning text-white"
                      >
                        Update
                      </Button>
                    </>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="mt-3">
        <Pagination
          current={pagination.current}
          onChange={(page) =>
            setPagination((prev) => ({ ...prev, current: page }))
          }
          total={total}
        />
      </div>

      {contextHolder}
      <Drawer
        title="Update User Detail"
        width={500}
        onClose={onClose}
        open={open}
      >
        <Form layout="vertical" form={form} onFinish={onFinish}>
          <div className="row">
            <Form.Item className="d-none" name="user_id">
              <Input />
            </Form.Item>
            <Form.Item rules={[{ required: true }]} label="Name" name="name">
              <Input placeholder="User Name" />
            </Form.Item>
            <div className="col-md-6">
              <Form.Item
                rules={[{ required: true }]}
                label="Email"
                name="user_email"
              >
                <Input placeholder="User Email" />
              </Form.Item>
            </div>
            <div className="col-md-6">
              <Form.Item
                rules={[{ required: true }]}
                label="Phone"
                name="number"
              >
                <Input placeholder="User Phone" />
              </Form.Item>
            </div>
            <div className="col-md-6">
              <Form.Item
                rules={[{ required: true }]}
                label="Gender"
                name="gander"
              >
                <Select
                  placeholder="Select Gender"
                  options={[
                    { value: "male", label: "Male" },
                    { value: "female", label: "Female" },
                  ]}
                />
              </Form.Item>
            </div>
            <div className="col-md-6">
              <Form.Item
                rules={[{ required: true }]}
                label="Date Of Birth"
                name="dob"
              >
                <DatePicker
                  format="DD-MM-YYYY"
                  placeholder="Date Of Birth"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </div>
            <Form.Item
              rules={[{ required: true }]}
              label="Address One"
              name="address_one"
            >
              <TextArea placeholder="Address One" maxLength={200} showCount />
            </Form.Item>
            <Form.Item
              rules={[{ required: true }]}
              label="Address Two"
              name="address_two"
            >
              <TextArea placeholder="Address Two" maxLength={200} showCount />
            </Form.Item>
            <div className="col-md-6">
              <Form.Item rules={[{ required: true }]} label="City" name="city">
                <Input placeholder="City" />
              </Form.Item>
            </div>
            <div className="col-md-6">
              <Form.Item
                rules={[{ required: true }]}
                label="State"
                name="state"
              >
                <Input placeholder="State" />
              </Form.Item>
            </div>
            <div className="col-md-6">
              <Form.Item
                rules={[{ required: true }]}
                label="Country"
                name="country"
              >
                <Input placeholder="Country" />
              </Form.Item>
            </div>
            <div className="col-md-6">
              <Form.Item
                rules={[{ required: true }]}
                label="ZipCode"
                name="zip_code"
              >
                <Input placeholder="ZipCode" />
              </Form.Item>
            </div>
            <div className="col-md-6">
              <Form.Item
                label="Nature Of Business Activity"
                name="business_activity"
              >
                <Select
                  options={[
                    { label: "Jeweler", value: "Jeweler" },
                    { label: "Diamond Broker", value: "Diamond Broker" },
                    { label: "Individual", value: "Individual" },
                  ]}
                />
              </Form.Item>
            </div>
            <div className="col-md-6">
              <Form.Item label="Company Name" name="company_name">
                <Input placeholder="Company Name" />
              </Form.Item>
            </div>
            <div className="col-md-6">
              <Form.Item label="Website" name="website">
                <Input placeholder="Website" />
              </Form.Item>
            </div>
            <div className="col-md-6">
              <Form.Item
                label="Business Relationship Period"
                name="business_relationship_period"
              >
                <Input placeholder="Business Relationship Period" />
              </Form.Item>
            </div>
            <div className="col-md-6">
              <Form.Item label="Promoter Name" name="promoter_name">
                <Input placeholder="Promoter Name" />
              </Form.Item>
            </div>
            <div className="col-md-6">
              <Form.Item
                label="Business Registration No"
                name="business_registrarion_no"
              >
                <Input placeholder="Business Registration No" />
              </Form.Item>
            </div>
            <Form.Item rules={[{ required: true }]}>
              <Button htmlType="submit" className="bg-success text-white">
                Update Detail
              </Button>
            </Form.Item>
          </div>
        </Form>
      </Drawer>
    </div>
  );
};

export default UserList;
